import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CATEGORIES,
  MAX_BRAND_GALLERY_ITEMS,
  MAX_GALLERY_CAPTION_LENGTH,
  MAX_UPLOAD_BYTES,
} from '@affily/shared/constants';
import { formatPercent } from '@affily/shared/money';
import { youtubeVideoId, youtubeWatchUrl } from '@affily/shared/media';
import { api } from '../lib/api.js';
import { TermsInline } from './Terms.jsx';
import { Reviews } from './Reviews.jsx';
import { Gallery, GalleryThumb } from './Gallery.jsx';
import { UploadButton } from './UploadButton.jsx';
import { DraftBanner, UnpublishControl } from './DraftBanner.jsx';
import { Button, Badge, EmptyState, Unknown, Input, Textarea, Alert } from './ui.jsx';

const EMPTY = {
  companyName: '',
  website: '',
  description: '',
  location: '',
  logoUrl: '',
  coverUrl: '',
  categories: [],
  gallery: [],
};

// What the upload endpoint accepts (it checks by content, not by these).
const IMAGE_TYPES = 'image/png,image/jpeg,image/gif,image/webp';
const MEDIA_TYPES = `${IMAGE_TYPES},video/mp4,video/webm`;

const ITEM_LABELS = { image: 'Photo', video: 'Video clip', youtube: 'YouTube' };

const toForm = (brand) =>
  brand
    ? {
        ...EMPTY,
        companyName: brand.companyName ?? '',
        website: brand.website ?? '',
        description: brand.description ?? '',
        location: brand.location ?? '',
        logoUrl: brand.logoUrl ?? '',
        coverUrl: brand.coverUrl ?? '',
        categories: brand.categories ?? [],
        gallery: brand.gallery ?? [],
      }
    : EMPTY;

/** A website link's host, or nothing while someone is still typing it. */
function hostOf(url) {
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

const sectionTitle = 'mb-2 text-2xs font-semibold uppercase tracking-wider text-ink-faint';

/**
 * A brand's page — the public one, and the one its owner edits.
 *
 * There is one page, not a page and a form: the owner presses "Edit page" and
 * each part of it turns into its own field, in place, so what they are filling
 * in is what creators will see. "Preview" shows the page exactly as published,
 * unsaved changes included. Nothing goes live until Save.
 *
 * `brand` null is a brand being created ("+ Add a brand", or the first one):
 * the page starts empty and in edit mode, and Save creates it.
 */
export function BrandPage({
  brand,
  programs = [],
  canEdit = false,
  startEditing = false,
  onSaved,
  onCancelNew,
}) {
  const creating = !brand;
  const initial = useMemo(() => toForm(brand), [brand]);
  const [form, setForm] = useState(initial);
  const [editing, setEditing] = useState(creating || startEditing);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [uploadError, setUploadError] = useState('');
  const [saved, setSaved] = useState(false);

  // A fresh copy from the server (after a save, or another brand) resets the edit.
  useEffect(() => setForm(initial), [initial]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);

  // Leaving with unsaved edits asks first, as any editor would.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const update = (patch) => {
    setForm((f) => ({ ...f, ...(typeof patch === 'function' ? patch(f) : patch) }));
    setSaved(false);
  };
  const set = (key) => (e) => update({ [key]: e.target.value });
  const toggleCategory = (c) =>
    update((f) => ({
      categories: f.categories.includes(c)
        ? f.categories.filter((x) => x !== c)
        : [...f.categories, c],
    }));

  function discard() {
    setForm(initial);
    setFieldErrors({});
    setError('');
    if (creating) onCancelNew?.();
    else setEditing(false);
  }

  async function save() {
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      // Every field is sent, empty ones as '': that is how a cleared website
      // or removed cover gets cleared.
      const payload = {
        companyName: form.companyName,
        website: form.website,
        description: form.description,
        location: form.location,
        logoUrl: form.logoUrl,
        coverUrl: form.coverUrl,
        categories: form.categories,
        gallery: form.gallery.map(({ type, url, caption }) => ({ type, url, caption })),
      };
      const result = creating
        ? await api.post('/brands', payload)
        : // This page's brand, which need not be the dashboard's current one.
          await api.patch('/brands/me', payload, { headers: { 'X-Affily-Brand': brand.id } });
      // The parent reloads the brand first, so the page never flashes the
      // old copy as "unsaved changes" in between.
      await onSaved?.(result.brand);
      setSaved(true);
      setEditing(false);
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.details ?? {});
      setEditing(true);
    } finally {
      setBusy(false);
    }
  }

  const showCover = editing || Boolean(form.coverUrl);
  const logoSize = showCover
    ? '-mt-8 size-20 text-2xl ring-4 ring-paper sm:-mt-10 sm:size-24'
    : 'size-16 text-xl';
  const host = hostOf(form.website);

  return (
    <div className={canEdit && (editing || dirty) ? 'pb-24' : ''}>
      {canEdit && !creating && (
        <DraftBanner profile={brand} kind="brand" onPublished={() => onSaved?.()} />
      )}

      {canEdit && !editing && !dirty && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-rule bg-paper-raised px-4 py-2.5">
          <p className="text-sm text-ink-muted">
            {saved
              ? 'Saved. This is your page as creators see it.'
              : brand?.isPublished === false
                ? 'Your page — only you can see it until you publish.'
                : 'Your page, as creators see it.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button to="/dashboard/brand/new" variant="outline" size="sm">
              + Add another brand
            </Button>
            <Button size="sm" onClick={() => setEditing(true)}>
              Edit page
            </Button>
          </div>
        </div>
      )}

      {/* ── Cover ─────────────────────────────────────────────────────── */}
      {showCover && (
        <div className="relative mt-4">
          {form.coverUrl ? (
            <img
              src={form.coverUrl}
              alt=""
              className="aspect-[3/1] w-full rounded-xl border border-rule bg-paper-sunken object-cover"
            />
          ) : (
            <div className="flex aspect-[3/1] w-full items-center justify-center rounded-xl border border-dashed border-rule-strong px-4 text-center text-sm text-ink-faint">
              Add a cover photo: a wide image, around 1500 × 500
            </div>
          )}
          {editing && (
            <div className="absolute bottom-3 right-3 flex gap-2">
              <UploadButton
                accept={IMAGE_TYPES}
                onUploaded={(file) => update({ coverUrl: file.url })}
                onError={setUploadError}
              >
                {form.coverUrl ? 'Change cover' : 'Upload cover'}
              </UploadButton>
              {form.coverUrl && (
                <Button variant="outline" size="sm" onClick={() => update({ coverUrl: '' })}>
                  Remove
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {editing && <FieldError>{fieldErrors.coverUrl}</FieldError>}

      {/* ── Header ────────────────────────────────────────────────────── */}
      {/* With a cover, the logo overlaps its lower edge, like a social profile.
          `relative` keeps the logo (and its ring) painted above the cover. */}
      <header className={`flex items-start gap-4 ${showCover ? 'px-3 sm:px-6' : 'mt-4'}`}>
        <div className="shrink-0">
          {form.logoUrl ? (
            <img
              src={form.logoUrl}
              alt=""
              className={`relative rounded-lg border border-rule bg-paper-raised object-cover ${logoSize}`}
            />
          ) : (
            <div
              className={`relative flex items-center justify-center rounded-lg border border-rule bg-paper-sunken font-semibold text-ink-faint ${logoSize}`}
            >
              {form.companyName.charAt(0) || '?'}
            </div>
          )}
        </div>

        <div className={`min-w-0 flex-1 ${showCover ? 'pt-3' : ''}`}>
          {editing ? (
            <>
              <input
                value={form.companyName}
                onChange={set('companyName')}
                maxLength={120}
                required
                placeholder="Company name"
                aria-label="Company name"
                className="w-full rounded-md border border-rule-strong bg-paper-raised px-2 py-1 text-3xl font-semibold tracking-tight text-ink placeholder:text-ink-faint focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
              />
              <FieldError>{fieldErrors.companyName}</FieldError>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                <div>
                  <Input
                    value={form.location}
                    onChange={set('location')}
                    maxLength={120}
                    placeholder="Location, e.g. Sofia, Bulgaria"
                    aria-label="Location"
                  />
                  <FieldError>{fieldErrors.location}</FieldError>
                </div>
                <div>
                  <Input
                    type="url"
                    value={form.website}
                    onChange={set('website')}
                    placeholder="Website, https://example.com"
                    aria-label="Website"
                  />
                  <FieldError>{fieldErrors.website}</FieldError>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <UploadButton
                  accept={IMAGE_TYPES}
                  variant="ghost"
                  onUploaded={(file) => update({ logoUrl: file.url })}
                  onError={setUploadError}
                >
                  {form.logoUrl ? 'Change logo' : 'Upload logo'}
                </UploadButton>
                {form.logoUrl && (
                  <Button variant="ghost" size="sm" onClick={() => update({ logoUrl: '' })}>
                    Remove logo
                  </Button>
                )}
              </div>
              <FieldError>{fieldErrors.logoUrl}</FieldError>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-semibold tracking-tight">
                {form.companyName || 'Your brand'}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {form.location && <Badge>{form.location}</Badge>}
                {form.categories.map((c) => (
                  <Badge key={c} className="capitalize">
                    {c}
                  </Badge>
                ))}
                {host && (
                  <a
                    href={form.website}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-xs text-accent hover:underline"
                  >
                    {host}
                  </a>
                )}
              </div>
            </>
          )}
        </div>
      </header>

      {editing && (
        <fieldset className="mt-5">
          <legend className={sectionTitle}>Categories</legend>
          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => toggleCategory(c)}
                aria-pressed={form.categories.includes(c)}
              >
                <Badge tone={form.categories.includes(c) ? 'accent' : 'neutral'} className="capitalize">
                  {c}
                </Badge>
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {/* ── Product / service ─────────────────────────────────────────── */}
      {editing ? (
        <section className="mt-6 max-w-2xl">
          <label htmlFor="brand-description" className={`block ${sectionTitle}`}>
            Product / service
          </label>
          <Textarea
            id="brand-description"
            value={form.description}
            onChange={set('description')}
            maxLength={2000}
            rows={5}
            placeholder="What you offer and who it is for: a hotel, an app, a free game with in-game purchases…"
          />
          <p className="mt-1 text-xs text-ink-faint">
            Creators read this before deciding whether your audience is theirs.{' '}
            {form.description.length}/2000
          </p>
          <FieldError>{fieldErrors.description}</FieldError>
        </section>
      ) : (
        form.description && (
          <p className="mt-6 max-w-2xl whitespace-pre-line text-ink-muted">{form.description}</p>
        )
      )}

      <Alert className="mt-4">{uploadError}</Alert>

      {/* ── Programs ──────────────────────────────────────────────────── */}
      {!creating && (
        <section className="mt-10">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className={sectionTitle}>Programs · full terms public</h2>
            {editing && (
              // A full load, so the dashboard opens on this brand (see useBrand).
              <a
                href={`/dashboard/brand/programs?brand=${brand.id}`}
                className="text-xs text-accent hover:underline"
              >
                Programs are edited in the dashboard →
              </a>
            )}
          </div>
          {programs.length === 0 ? (
            <EmptyState title="No live programs right now" />
          ) : (
            <ProgramsTable programs={programs} />
          )}
        </section>
      )}

      {/* ── Gallery ───────────────────────────────────────────────────── */}
      {editing ? (
        <GalleryEditor
          gallery={form.gallery}
          fieldErrors={fieldErrors}
          onChange={(change) => update((f) => ({ gallery: change(f.gallery) }))}
        />
      ) : (
        form.gallery.length > 0 && (
          <section className="mt-10">
            <h2 className={sectionTitle}>Gallery</h2>
            <Gallery items={form.gallery} />
          </section>
        )
      )}

      {!creating && <Reviews subjectType="brand" slug={brand.slug} />}

      {canEdit && !creating && <UnpublishControl profile={brand} kind="brand" onChanged={() => onSaved?.()} />}

      {/* ── Save bar ──────────────────────────────────────────────────── */}
      {canEdit && (editing || dirty) && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-rule bg-paper-raised/95 backdrop-blur">
          <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
            {/* On a phone the buttons say enough; only an error needs the room. */}
            <p
              className={`min-w-0 flex-1 text-sm text-ink-muted ${error ? 'basis-full sm:basis-auto' : 'hidden sm:block'}`}
            >
              {error ? (
                <span className="font-medium text-negative">{error}</span>
              ) : editing ? (
                creating ? (
                  'Fill in your page. It is created when you save.'
                ) : (
                  'Editing. Nothing changes for visitors until you save.'
                )
              ) : (
                'Preview, with your unsaved changes.'
              )}
            </p>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={discard} disabled={busy}>
                {creating ? 'Cancel' : 'Discard'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setEditing(!editing)} disabled={busy}>
                {editing ? 'Preview' : 'Keep editing'}
              </Button>
              <Button size="sm" onClick={save} disabled={busy || !form.companyName.trim()}>
                {busy ? 'Saving…' : creating ? 'Create brand' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldError({ children }) {
  if (!children) return null;
  return (
    <p role="alert" className="mt-1 text-xs font-medium text-negative">
      {children}
    </p>
  );
}

function ProgramsTable({ programs }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
      <table className="data-table">
        <thead>
          <tr>
            <th>Program</th>
            <th>Terms</th>
            <th className="num">Partners</th>
            <th className="num">Conv. rate</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {programs.map((p) => (
            <tr key={p.id}>
              <td>
                <Link to={`/programs/${p.slug}`} className="font-medium text-ink hover:text-accent">
                  {p.title}
                </Link>
                {p.status !== 'active' && (
                  <Badge tone="caution" className="ml-2">
                    {p.status}
                  </Badge>
                )}
              </td>
              <td>
                <TermsInline program={p} />
              </td>
              <td className="num">{p.performance.activePartnerships}</td>
              <td className="num">
                {p.performance.conversionRatePct === null ? (
                  <Unknown reason="No tracked clicks yet" />
                ) : (
                  formatPercent(p.performance.conversionRatePct)
                )}
              </td>
              <td className="num">
                <Link to={`/programs/${p.slug}`} className="text-xs text-accent hover:underline">
                  View terms
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The gallery, in place: reorder, caption, remove, and add photos, clips or
 * YouTube. `onChange` takes an updater, since several uploads can land in a row.
 */
function GalleryEditor({ gallery, fieldErrors, onChange }) {
  const [youtubeLink, setYoutubeLink] = useState('');
  const [error, setError] = useState('');
  const full = gallery.length >= MAX_BRAND_GALLERY_ITEMS;

  // Uploads land one at a time; each adds to whatever the gallery holds by then.
  const add = (item) => onChange((g) => (g.length >= MAX_BRAND_GALLERY_ITEMS ? g : [...g, item]));
  const updateItem = (i, patch) =>
    onChange((g) => g.map((item, j) => (j === i ? { ...item, ...patch } : item)));
  const remove = (i) => onChange((g) => g.filter((_, j) => j !== i));
  const move = (i, delta) =>
    onChange((g) => {
      const next = [...g];
      [next[i], next[i + delta]] = [next[i + delta], next[i]];
      return next;
    });

  function addYouTube() {
    const id = youtubeVideoId(youtubeLink);
    if (!id) {
      setError('That isn’t a link to a YouTube video. Copy it from Share under the video.');
      return;
    }
    setError('');
    add({ type: 'youtube', url: youtubeWatchUrl(id), youtubeId: id, caption: '' });
    setYoutubeLink('');
  }

  return (
    <section className="mt-10 space-y-3">
      <div>
        <h2 className={sectionTitle}>
          Gallery · {gallery.length}/{MAX_BRAND_GALLERY_ITEMS}
        </h2>
        <p className="text-xs text-ink-faint">
          Product photos, short clips (up to {MAX_UPLOAD_BYTES / 1024 / 1024}MB) and YouTube videos,
          shown in this order.
        </p>
      </div>

      <FieldError>{fieldErrors.gallery}</FieldError>

      {gallery.length > 0 && (
        <ol className="space-y-2">
          {gallery.map((item, i) => {
            // This item's own errors only; a list-level one shows above.
            const itemError = fieldErrors[`gallery.${i}.url`] ?? fieldErrors[`gallery.${i}.caption`];
            return (
              <li
                key={`${i}-${item.youtubeId ?? item.url}`}
                className="flex items-center gap-3 rounded-lg border border-rule bg-paper-raised p-2"
              >
                <div className="h-14 w-20 shrink-0 overflow-hidden rounded bg-paper-sunken">
                  <GalleryThumb item={item} />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                    {ITEM_LABELS[item.type]}
                  </span>
                  <Input
                    value={item.caption ?? ''}
                    onChange={(e) => updateItem(i, { caption: e.target.value })}
                    maxLength={MAX_GALLERY_CAPTION_LENGTH}
                    placeholder="Caption (optional)"
                    aria-label={`Caption for item ${i + 1}`}
                    className="mt-0.5"
                  />
                  <FieldError>{itemError}</FieldError>
                </div>
                <div className="flex shrink-0 items-center gap-1 text-xs">
                  <button
                    type="button"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    aria-label={`Move item ${i + 1} earlier`}
                    className="rounded px-1.5 py-1 text-ink-muted hover:bg-paper-sunken disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => move(i, 1)}
                    disabled={i === gallery.length - 1}
                    aria-label={`Move item ${i + 1} later`}
                    className="rounded px-1.5 py-1 text-ink-muted hover:bg-paper-sunken disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="px-1.5 py-1 text-negative hover:underline"
                  >
                    Remove
                  </button>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {full ? (
        <p className="text-xs text-ink-muted">The gallery is full. Remove something to add more.</p>
      ) : (
        <div className="space-y-3 rounded-lg border border-dashed border-rule-strong p-3">
          <UploadButton
            accept={MEDIA_TYPES}
            multiple
            onUploaded={(file) =>
              add({
                type: file.mime?.startsWith('video/') ? 'video' : 'image',
                url: file.url,
                caption: '',
              })
            }
            onError={setError}
          >
            Upload photos or clips
          </UploadButton>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="url"
              value={youtubeLink}
              onChange={(e) => setYoutubeLink(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addYouTube();
                }
              }}
              placeholder="https://youtu.be/…"
              aria-label="YouTube video link"
              className="min-w-56 flex-1"
            />
            <Button variant="outline" size="sm" onClick={addYouTube} disabled={!youtubeLink.trim()}>
              Add YouTube video
            </Button>
          </div>
        </div>
      )}
      <Alert>{error}</Alert>
    </section>
  );
}
